import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import {
  Bell, Check, CheckCheck, Filter, Inbox,
  FileText, CreditCard, MessageSquare, FolderKanban, Wrench, Star,
} from "lucide-react";

/**
 * Full notification inbox (Tesla / Linear style).
 *
 * Tabs: All, Unread, by-type.
 * Click row -> mark read + deep link.
 * "Mark all read" button.
 */
const TYPE_META = {
  invoice:        { icon: CreditCard, label: "Billing",  color: "text-rose-500 bg-rose-50" },
  payment:        { icon: CreditCard, label: "Billing",  color: "text-emerald-500 bg-emerald-50" },
  project:        { icon: FolderKanban, label: "Project", color: "text-blue-500 bg-blue-50" },
  project_completed: { icon: Star,    label: "Project", color: "text-amber-500 bg-amber-50" },
  work_order:     { icon: Wrench,     label: "Work order", color: "text-indigo-500 bg-indigo-50" },
  message:        { icon: MessageSquare, label: "Message", color: "text-violet-500 bg-violet-50" },
  estimate:       { icon: FileText,   label: "Estimate", color: "text-cyan-600 bg-cyan-50" },
  daily_log:      { icon: FileText,   label: "Daily log", color: "text-emerald-600 bg-emerald-50" },
  default:        { icon: Bell,       label: "General",  color: "text-slate-500 bg-slate-100" },
};

function metaFor(type) {
  return TYPE_META[type] || TYPE_META.default;
}

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diffS = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diffS < 60) return "just now";
  const m = Math.floor(diffS / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unread | <type>

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications", { params: { limit: 100 } });
      setItems(r.data);
    } catch (err) {
      console.warn("notifs fetch:", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const types = useMemo(() => {
    const set = new Set();
    items.forEach((n) => n.type && set.add(n.type));
    return Array.from(set);
  }, [items]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "unread") return items.filter((n) => !n.read);
    return items.filter((n) => n.type === filter);
  }, [items, filter]);

  const unreadCount = items.filter((n) => !n.read).length;

  const open = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.notification_id}/read`);
        setItems((arr) => arr.map((x) => x.notification_id === n.notification_id ? { ...x, read: true } : x));
        window.dispatchEvent(new CustomEvent("pb:notification"));
      } catch (err) {
        console.warn("mark read:", err);
      }
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      window.dispatchEvent(new CustomEvent("pb:notification"));
      toast.success("All caught up");
    } catch (err) {
      toast.error("Couldn't mark all read");
    }
  };

  return (
    <div data-testid="notifications-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="overline">Inbox</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Notifications</h1>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
            {unreadCount > 0 ? <><strong className="text-[#C9A356]">{unreadCount}</strong> unread · </> : null}
            {items.length} total
          </p>
        </div>
        <button
          onClick={markAll}
          disabled={unreadCount === 0}
          data-testid="notifications-mark-all"
          className="btn-outline-navy text-sm disabled:opacity-50"
        >
          <CheckCheck className="w-4 h-4 mr-2" /> Mark all read
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1.5 flex-wrap mb-4" data-testid="notifications-filters">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all">
          <Inbox className="w-3.5 h-3.5" /> All ({items.length})
        </FilterChip>
        <FilterChip active={filter === "unread"} onClick={() => setFilter("unread")} testId="filter-unread">
          <Filter className="w-3.5 h-3.5" /> Unread ({unreadCount})
        </FilterChip>
        {types.map((t) => {
          const m = metaFor(t);
          const count = items.filter((n) => n.type === t).length;
          const Icon = m.icon;
          return (
            <FilterChip key={t} active={filter === t} onClick={() => setFilter(t)} testId={`filter-${t}`}>
              <Icon className="w-3.5 h-3.5" /> {m.label} ({count})
            </FilterChip>
          );
        })}
      </div>

      <div className="card-pb overflow-hidden">
        {loading ? (
          <div className="px-6 py-16 text-center text-sm text-[#475569]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-16 text-center" data-testid="notifications-empty">
            <Bell className="w-12 h-12 mx-auto text-[#E2E8F0] dark:text-slate-700 mb-3" />
            <div className="text-[#0A2540] dark:text-white font-medium">You're all caught up</div>
            <div className="text-sm text-[#475569] dark:text-slate-400 mt-1">New notifications will appear here.</div>
          </div>
        ) : (
          <ul>
            {filtered.map((n) => {
              const m = metaFor(n.type);
              const Icon = m.icon;
              return (
                <li key={n.notification_id}>
                  <button
                    type="button"
                    onClick={() => open(n)}
                    data-testid={`notif-row-${n.notification_id}`}
                    className={`w-full text-left flex items-start gap-4 px-5 py-4 border-b border-[#E2E8F0] dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-900/40 transition ${
                      !n.read ? "bg-[#FFFBF0]/60 dark:bg-amber-900/10" : ""
                    }`}
                  >
                    <span className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${m.color}`}>
                      <Icon className="w-5 h-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {!n.read && <span className="w-2 h-2 rounded-full bg-[#C9A356] shrink-0" aria-label="unread" />}
                        <div className={`text-sm truncate ${!n.read ? "font-semibold" : "font-medium"} text-[#0A2540] dark:text-white`}>
                          {n.title}
                        </div>
                      </div>
                      {n.body && <div className="text-sm text-[#475569] dark:text-slate-400 mt-0.5 line-clamp-2">{n.body}</div>}
                      <div className="text-[11px] text-[#94A3B8] mt-1">
                        {m.label} · {timeAgo(n.created_at)}
                      </div>
                    </div>
                    {n.read && <Check className="w-4 h-4 text-[#94A3B8] shrink-0 mt-1" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${
        active
          ? "bg-[#0A2540] text-white shadow-sm"
          : "bg-slate-100 dark:bg-slate-800 text-[#475569] dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
      }`}
    >
      {children}
    </button>
  );
}
